import React, { useEffect, useRef, useMemo } from "react";
import Highcharts from "highcharts";
import { useQuery } from "@tanstack/react-query";
import { fetchTeamTrends } from "../../services/api/analytics";
import CountUp from "react-countup";

const TeamTrendsChart = ({ filterParams = {} }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);

  const { data: result, isFetching } = useQuery({
    queryKey: [
      "analytics-teamTrends",
      filterParams.clientId,
      filterParams.startDate,
      filterParams.endDate,
    ],
    queryFn: () =>
      fetchTeamTrends({
        ...(filterParams.clientId ? { clientId: filterParams.clientId } : {}),
        ...(filterParams.startDate ? { startDate: filterParams.startDate } : {}),
        ...(filterParams.endDate ? { endDate: filterParams.endDate } : {}),
      }),
    staleTime: 60_000,
  });

  const trends = result?.data;

  // Sum each team's data array into a single pie slice value
  const pieData = useMemo(() => {
    if (!trends?.series?.length) return [];
    return trends.series
      .map((s) => ({
        name: s.name,
        y: (s.data ?? []).reduce((acc, v) => acc + (v || 0), 0),
        color: s.color,
      }))
      .filter((s) => s.y > 0);
  }, [trends]);

  const pieTotal = useMemo(() => pieData.reduce((acc, s) => acc + s.y, 0), [pieData]);

  useEffect(() => {
    if (!containerRef.current) return;

    chartRef.current = Highcharts.chart(containerRef.current, {
      credits: { enabled: false },
      accessibility: { enabled: false },
      chart: {
        type: "pie",
        height: 370,
        style: { fontFamily: "Poppins, sans-serif" },
        backgroundColor: "transparent",
        animation: { duration: 400 },
      },
      title: { text: null },
      tooltip: {
        backgroundColor: "#1e293b",
        borderWidth: 0,
        borderRadius: 10,
        shadow: false,
        style: { color: "#f1f5f9", fontSize: "12px" },
        pointFormat:
          '<span style="color:{point.color}">●</span> {series.name}: <b>{point.y}</b> ({point.percentage:.1f}%)<br/>',
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: "pointer",
          borderWidth: 2,
          borderColor: "#fff",
          dataLabels: {
            enabled: true,
            format: "<b>{point.name}</b><br/>{point.y} ({point.percentage:.1f}%)",
            style: { fontSize: "10px", fontWeight: "500", color: "#64748b" },
            distance: 18,
          },
          showInLegend: true,
        },
      },
      legend: {
        layout: "horizontal",
        align: "center",
        verticalAlign: "bottom",
        symbolRadius: 50,
        symbolHeight: 8,
        symbolWidth: 8,
        itemStyle: { fontSize: "11px", fontWeight: "500", color: "#64748b" },
      },
      series: [{ name: "Work Orders", colorByPoint: true, data: [] }],
    });

    requestAnimationFrame(() => {
      chartRef.current?.reflow();
    });

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!chartRef.current || !pieData.length) return;
    chartRef.current.series[0]?.setData(pieData, true, { duration: 400 });
  }, [pieData]);

  const isEmpty = !isFetching && pieData.length === 0;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-800 text-sm">Team Work Order Distribution</h3>
          <p className="text-[11px] text-gray-400 mt-0.5">All teams · share of work orders</p>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-gray-700">
            <CountUp start={0} end={pieTotal} duration={1.2} />
          </p>
          <p className="text-[10px] text-gray-400">Total</p>
        </div>
      </div>

      {isFetching && (
        <div className="absolute inset-0 bg-white/60 rounded-2xl flex items-center justify-center z-10">
          <i className="fa-solid fa-circle-notch fa-spin text-primary text-lg" />
        </div>
      )}

      {isEmpty ? (
        <div className="flex items-center justify-center flex-1 text-gray-400 text-sm">
          No team data available
        </div>
      ) : (
        <div ref={containerRef} style={{ width: "100%", flex: 1 }} />
      )}
    </div>
  );
};

export default TeamTrendsChart;
